import React from 'react';
import type { LucideIcon } from 'lucide-react';

/**
 * Single coloured tile used in the Photo Sources stats row.
 * Extracted so Impact / DAM / Upload tabs render identical-looking tiles
 * while each owns its own data — no shared, leaky stats state.
 *
 * Tailwind's JIT needs static class strings, so palette → classes is a
 * lookup table rather than template-interpolation.
 */

export type StatVariant = 'blue' | 'green' | 'purple' | 'orange';

interface PaletteClasses {
  bg: string;
  label: string;
  value: string;
  icon: string;
}

const PALETTE: Record<StatVariant, PaletteClasses> = {
  blue:   { bg: 'bg-blue-50',   label: 'text-blue-900',   value: 'text-blue-600',   icon: 'text-blue-600' },
  green:  { bg: 'bg-green-50',  label: 'text-green-900',  value: 'text-green-600',  icon: 'text-green-600' },
  purple: { bg: 'bg-purple-50', label: 'text-purple-900', value: 'text-purple-600', icon: 'text-purple-600' },
  orange: { bg: 'bg-orange-50', label: 'text-orange-900', value: 'text-orange-600', icon: 'text-orange-600' },
};

interface StatCardProps {
  icon: LucideIcon;
  label: string;
  /** Big bold value; `0` is allowed so callers don't need to stringify. */
  value: React.ReactNode;
  variant: StatVariant;
  /** Whether to render the value with the smaller font used for text-y
   *  fields (e.g. status filters / program names). Numeric counters
   *  keep the default large-font treatment. */
  compact?: boolean;
}

export const StatCard: React.FC<StatCardProps> = ({ icon: Icon, label, value, variant, compact }) => {
  const palette = PALETTE[variant];
  return (
    <div className={`${palette.bg} rounded-lg p-4`}>
      <div className="flex items-center">
        <Icon className={`h-8 w-8 ${palette.icon}`} />
        <div className="ml-3 min-w-0">
          <p className={`text-sm font-medium ${palette.label}`}>{label}</p>
          <p
            className={`${
              compact ? 'text-sm font-semibold' : 'text-2xl font-bold'
            } ${palette.value} truncate`}
          >
            {value}
          </p>
        </div>
      </div>
    </div>
  );
};

/** Standard 1/4-column grid wrapper to keep horizontal alignment with the page header. */
export const StatGrid: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">{children}</div>
);
